function decodeXmlEntities(value: string): string {
  return value
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&apos;/g, "'");
}

export function extractDocxPlainText(xml: string): string {
  const normalized = xml
    .replace(/<w:tab\b[^>]*\/>/g, "<w:t>\t</w:t>")
    .replace(/<w:br\b[^>]*\/>/g, "<w:t>\n</w:t>")
    .replace(/<w:cr\b[^>]*\/>/g, "<w:t>\n</w:t>");

  const paragraphs = normalized.split(/<\/w:p>/);
  const lines: string[] = [];
  for (const paragraph of paragraphs) {
    const parts: string[] = [];
    const textNode = /<w:t(?:\s[^>]*)?>([^<]*)<\/w:t>/g;
    let match = textNode.exec(paragraph);
    while (match) {
      parts.push(decodeXmlEntities(match[1]));
      match = textNode.exec(paragraph);
    }
    if (parts.length > 0) {
      lines.push(parts.join(""));
    }
  }
  return lines
    .join("\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

function readU16(view: DataView, offset: number): number {
  return view.getUint16(offset, true);
}

function readU32(view: DataView, offset: number): number {
  return view.getUint32(offset, true);
}

function findEndOfCentralDirectory(bytes: Uint8Array, view: DataView): number {
  for (let offset = bytes.length - 22; offset >= 0; offset -= 1) {
    if (readU32(view, offset) === 0x06054b50) {
      return offset;
    }
  }
  throw new Error("Invalid DOCX zip");
}

async function inflateRaw(data: Uint8Array): Promise<Uint8Array> {
  if (typeof DecompressionStream === "undefined") {
    throw new Error("DOCX inflate is not supported in this browser");
  }
  const stream = new Blob([data]).stream().pipeThrough(new DecompressionStream("deflate-raw"));
  return new Uint8Array(await new Response(stream).arrayBuffer());
}

async function readZipEntry(
  bytes: Uint8Array,
  view: DataView,
  localHeaderOffset: number,
  compressedSize: number,
  method: number
): Promise<Uint8Array> {
  if (readU32(view, localHeaderOffset) !== 0x04034b50) {
    throw new Error("Invalid DOCX local header");
  }
  const nameLen = readU16(view, localHeaderOffset + 26);
  const extraLen = readU16(view, localHeaderOffset + 28);
  const dataStart = localHeaderOffset + 30 + nameLen + extraLen;
  const compressed = bytes.subarray(dataStart, dataStart + compressedSize);
  if (method === 0) {
    return compressed;
  }
  if (method === 8) {
    return inflateRaw(compressed);
  }
  throw new Error("Unsupported DOCX compression");
}

async function unzipDocumentXml(buffer: ArrayBuffer): Promise<string> {
  const bytes = new Uint8Array(buffer);
  const view = new DataView(buffer);
  const eocd = findEndOfCentralDirectory(bytes, view);
  const cdCount = readU16(view, eocd + 10);
  let offset = readU32(view, eocd + 16);

  for (let index = 0; index < cdCount; index += 1) {
    if (readU32(view, offset) !== 0x02014b50) {
      throw new Error("Invalid DOCX central directory");
    }
    const method = readU16(view, offset + 10);
    const compressedSize = readU32(view, offset + 20);
    const nameLen = readU16(view, offset + 28);
    const extraLen = readU16(view, offset + 30);
    const commentLen = readU16(view, offset + 32);
    const localHeaderOffset = readU32(view, offset + 42);
    const name = new TextDecoder().decode(bytes.subarray(offset + 46, offset + 46 + nameLen));
    const normalized = name.replace(/\\/g, "/").replace(/^\//, "").toLowerCase();
    if (normalized === "word/document.xml") {
      const inflated = await readZipEntry(bytes, view, localHeaderOffset, compressedSize, method);
      return new TextDecoder("utf-8").decode(inflated);
    }
    offset += 46 + nameLen + extraLen + commentLen;
  }
  throw new Error("DOCX document.xml missing");
}

export async function parseDocxPreview(buffer: ArrayBuffer): Promise<string> {
  const xml = await unzipDocumentXml(buffer);
  const text = extractDocxPlainText(xml);
  if (!text) {
    throw new Error("DOCX has no readable text");
  }
  return text;
}
